import { motion } from 'framer-motion'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Cell,
  RadialBarChart, RadialBar, PolarAngleAxis,
} from 'recharts'
import {
  DollarSign, TrendingUp, Shield, Truck, Users2, PackageCheck, Zap,
  RefreshCw, CheckCircle2, CloudLightning, Route, Factory, Info,
} from 'lucide-react'
import { useFinancials } from '../hooks/useFinancials.js'
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx'
import Tooltip from '../components/shared/Tooltip.jsx'
import client from '../api/client.js'
import { useState, useEffect, useRef } from 'react'
import { trackEvent } from '../analytics/mixpanel.js'

// ── Counter animation hook ────────────────────────────────────────────────────

function useCountUp(target, duration = 1200) {
  const [value, setValue] = useState(0)
  const rafRef = useRef(null)
  const startRef = useRef(null)

  useEffect(() => {
    if (target === 0) { setValue(0); return }
    startRef.current = null
    const animate = (ts) => {
      if (!startRef.current) startRef.current = ts
      const progress = Math.min((ts - startRef.current) / duration, 1)
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(target * eased))
      if (progress < 1) rafRef.current = requestAnimationFrame(animate)
    }
    rafRef.current = requestAnimationFrame(animate)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [target, duration])

  return value
}

// ── Config ────────────────────────────────────────────────────────────────────

const CATEGORY_META = {
  LABOR_FLEX:               { label: 'Labor Flex',      color: '#22d3ee', icon: Users2 },
  LABOR_SURGE_PREP:         { label: 'Surge Prep',      color: '#06b6d4', icon: TrendingUp },
  ALLOCATION_HOLD:          { label: 'Hold Allocation', color: '#f59e0b', icon: Shield },
  REALLOCATION_OPPORTUNITY: { label: 'Reallocate',      color: '#d97706', icon: PackageCheck },
  PREDEPLOYMENT:            { label: 'Pre-deploy',      color: '#a78bfa', icon: PackageCheck },
  DC_YARD_CLEARANCE:        { label: 'Yard Clearance',  color: '#ef4444', icon: Zap },
  SURGE_WARNING:            { label: 'Surge Warning',   color: '#60a5fa', icon: CloudLightning },
  INBOUND_RISK:             { label: 'Inbound Risk',    color: '#fb7185', icon: Factory },
  LANE_RISK:                { label: 'Lane Risk',       color: '#818cf8', icon: Route },
}

const PILLAR_BREAKDOWN = [
  {
    key: 'estimated_labor_savings',
    label: 'Labor Savings',
    sub: 'Flexed shifts + surge staffing',
    color: '#22d3ee',
    icon: Users2,
    tip: 'Estimated savings from reducing variable shifts during low-traffic weather windows (LABOR_FLEX) and adding floor staff in the 48h pre-storm surge window (LABOR_SURGE_PREP). Calculated using a 22% labor-to-revenue ratio × variable shift share, augmented by actual shipment labor data where available.',
  },
  {
    key: 'total_trapped_capital_avoided',
    label: 'Capital Protected',
    sub: 'Held / redirected inventory',
    color: '#f59e0b',
    icon: Shield,
    tip: 'Estimated inventory value protected by pausing DC-to-store replenishment (ALLOCATION_HOLD) or redirecting allocations to higher sell-through stores (REALLOCATION_OPPORTUNITY). Derived from store sensitivity scores × weeks on hand × average weekly revenue. Uses pre-computed VaR margin risk when available.',
  },
  {
    key: 'freight_risk_avoided',
    label: 'Availability Safeguard',
    sub: 'Delays, yard clearance, pre-deploy',
    color: '#a78bfa',
    icon: Truck,
    tip: 'Estimated freight and logistics cost savings across four action types: PREDEPLOYMENT (pull-forward shipping before the storm window), DC_YARD_CLEARANCE (stage trailers before DC shutdown), INBOUND_RISK (delay inbound shipments to avoid detention charges), and LANE_RISK (reroute outbound lanes to avoid disruption).',
  },
]

const KPI_TIPS = {
  total: 'Sum of all estimated P&L impact across every active prescriptive action — labor savings + capital protection + freight risk avoidance. Recalculated every 30 minutes from live weather forecasts × store inventory data. Use the Refresh button to pull the latest immediately.',
  var_gauge: 'The share of total P&L potential that is tied to Immediate (24h) actions — ones where the storm or disruption window is already open or opens within 24 hours. A high percentage means the window to capture savings is closing fast and decisions need to be made today.',
  bar_chart: 'Estimated P&L savings broken out by action type. Each bar is the sum of pnl_estimate across all active actions of that category. Hover a bar for the exact dollar value. Taller bars = higher aggregate impact from that action type across all affected stores and DCs.',
  accepted: 'Count of actions that managers have clicked "Accept" on in the Action Center. Accepted decisions are logged to the database for future model training and audit purposes — they do not automatically execute any system changes.',
  refresh: 'Force-regenerates all prescriptive actions from the latest weather forecast data. The server auto-refreshes every 30 minutes in the background; use this only when you need the absolute latest data immediately (e.g. after a forecast update).',
}

function fmt(v) {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`
  return `$${v}`
}

// ── Gauge (RadialBarChart) ─────────────────────────────────────────────────────

function ValueAtRiskGauge({ total, immediate }) {
  const pct = total > 0 ? Math.min(100, Math.round((immediate / total) * 100)) : 0
  const gaugeData = [{ name: 'P&L at Risk', value: pct, fill: '#ef4444' }]

  return (
    <div className="glass-card p-6 flex flex-col items-center">
      <Tooltip content={KPI_TIPS.var_gauge} placement="bottom">
        <div className="flex items-center gap-1.5 mb-4 cursor-help">
          <div className="text-xs text-slate-500 uppercase tracking-widest">Value at Risk</div>
          <Info size={10} className="text-slate-600 flex-shrink-0" />
        </div>
      </Tooltip>
      <div className="relative w-48 h-48">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            innerRadius="65%"
            outerRadius="100%"
            startAngle={210}
            endAngle={-30}
            data={gaugeData}
          >
            <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
            <RadialBar
              background={{ fill: 'rgba(255,255,255,0.05)' }}
              dataKey="value"
              cornerRadius={8}
              angleAxisId={0}
            />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-black text-red-400">{pct}%</span>
          <span className="text-[10px] text-slate-500 mt-0.5">Immediate</span>
        </div>
      </div>
      <div className="mt-3 text-center">
        <div className="text-slate-400 text-xs">of total P&L potential is</div>
        <div className="text-red-400 text-sm font-bold">Immediate (24h) actions</div>
      </div>
    </div>
  )
}

// ── Bar chart tooltip ──────────────────────────────────────────────────────────

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const { name, value } = payload[0]
  return (
    <div
      className="px-3 py-2 rounded-lg text-xs border"
      style={{
        background: 'rgba(6,12,36,0.97)',
        borderColor: 'rgba(0,212,255,0.3)',
        color: '#e2e8f0',
      }}
    >
      <div className="font-bold text-cyan-300">{name}</div>
      <div className="text-emerald-400 font-black">{fmt(value)}</div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Financials() {
  const { data, isLoading, isError, refetch, isFetching } = useFinancials()
  const [refreshing, setRefreshing] = useState(false)

  const total = data?.total_pnl_potential ?? 0
  const animatedTotal = useCountUp(total)
  const animatedLabor = useCountUp(data?.estimated_labor_savings ?? 0)
  const animatedCapital = useCountUp(data?.total_trapped_capital_avoided ?? 0)
  const animatedFreight = useCountUp(data?.freight_risk_avoided ?? 0)

  const handleRefresh = async () => {
    trackEvent('Financials Refreshed', { source: 'manual' })
    setRefreshing(true)
    try {
      await client.post('/actions/refresh')
      await refetch()
    } finally {
      setRefreshing(false)
    }
  }

  // ≥50% visible for ≥1s → fire once per section per page visit. Gated on userScrolled
  // so initial mount (which can flash sections into view) doesn't auto-fire.
  const sectionsFiredRef = useRef(new Set())
  const sectionTimersRef = useRef(new Map())
  const userScrolledRef = useRef(false)
  useEffect(() => {
    const onScroll = () => {
      userScrolledRef.current = true
      // The IntersectionObserver below won't re-fire for sections already
      // intersecting at mount (no state change), so KPIs visible above the fold
      // would never trigger. On the first scroll, manually start timers for any
      // section currently ≥50% visible. The same fired-set / timers map dedupes
      // against the IO path.
      document.querySelectorAll('[data-section]').forEach((el) => {
        const rect = el.getBoundingClientRect()
        const vh = window.innerHeight || document.documentElement.clientHeight
        if (rect.height === 0) return
        const visible = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0))
        if (visible / rect.height < 0.5) return
        const name = el.getAttribute('data-section')
        if (!name) return
        if (sectionsFiredRef.current.has(name)) return
        if (sectionTimersRef.current.has(name)) return
        const t = setTimeout(() => {
          sectionTimersRef.current.delete(name)
          if (sectionsFiredRef.current.has(name)) return
          sectionsFiredRef.current.add(name)
          trackEvent('Financial Section Engaged', { section: name })
        }, 1000)
        sectionTimersRef.current.set(name, t)
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true, once: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  useEffect(() => {
    if (!data) return
    const timers = sectionTimersRef.current
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const name = entry.target.getAttribute('data-section')
        if (!name) continue
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (sectionsFiredRef.current.has(name)) continue
          if (timers.has(name)) continue
          const t = setTimeout(() => {
            timers.delete(name)
            if (sectionsFiredRef.current.has(name)) return
            if (!userScrolledRef.current) return
            sectionsFiredRef.current.add(name)
            trackEvent('Financial Section Engaged', { section: name })
          }, 1000)
          timers.set(name, t)
        } else {
          const t = timers.get(name)
          if (t) { clearTimeout(t); timers.delete(name) }
        }
      }
    }, { threshold: [0.5] })
    document.querySelectorAll('[data-section]').forEach((el) => observer.observe(el))
    return () => {
      observer.disconnect()
      timers.forEach((t) => clearTimeout(t))
      timers.clear()
    }
  }, [data])

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex justify-center items-center min-h-[60vh]">
        <LoadingSpinner />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 text-center text-slate-400">
        Failed to load financial data.
      </div>
    )
  }

  // Build bar-chart data from by_category
  const barData = Object.entries(data.by_category ?? {})
    .map(([cat, val]) => ({
      name: CATEGORY_META[cat]?.label ?? cat,
      value: val,
      color: CATEGORY_META[cat]?.color ?? '#64748b',
    }))
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value)

  // Map pillar keys to their animated values
  const animatedValues = {
    estimated_labor_savings: animatedLabor,
    total_trapped_capital_avoided: animatedCapital,
    freight_risk_avoided: animatedFreight,
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 xl:py-6 min-h-[calc(100vh-4rem)]">
      {/* ── Header ── */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-start justify-between flex-wrap gap-3 mb-6"
      >
        <div>
          <h1 className="text-2xl font-black text-slate-100 flex items-center gap-2">
            <DollarSign size={20} className="text-cyan-400" />
            ROI Simulator
            <Tooltip content="Financial impact of prescriptive actions — P&L potential across all active recommendations" placement="bottom" asButton>
              <Info size={15} className="text-slate-500 hover:text-slate-300 cursor-help transition-colors" />
            </Tooltip>
          </h1>
        </div>
        <Tooltip content={KPI_TIPS.refresh} placement="bottom">
          <button
            onClick={handleRefresh}
            disabled={refreshing || isFetching}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200 text-xs font-medium transition-colors disabled:opacity-50"
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </Tooltip>
      </motion.div>

      {/* ── KPI row ── */}
      <div data-section="kpi_cards" className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <motion.div
          className="glass-card p-4 col-span-2 sm:col-span-1"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
        >
          <Tooltip content={KPI_TIPS.total} placement="bottom">
            <div className="flex items-center gap-1.5 mb-1 cursor-help">
              <div className="text-xs text-slate-500 uppercase tracking-widest">Total P&L Potential</div>
              <Info size={10} className="text-slate-600 flex-shrink-0" />
            </div>
          </Tooltip>
          <div className="text-2xl font-black text-emerald-400">{fmt(animatedTotal)}</div>
          <div className="text-xs text-slate-500 mt-0.5">{data.action_count} active actions</div>
        </motion.div>

        {PILLAR_BREAKDOWN.map(({ key, label, sub, color, icon: Icon, tip }, i) => (
          <motion.div
            key={key}
            className="glass-card p-4"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: (i + 1) * 0.06 }}
          >
            <Tooltip content={tip} placement="bottom">
              <div className="flex items-center gap-1.5 mb-1 cursor-help">
                <Icon size={12} style={{ color }} />
                <div className="text-xs text-slate-500 uppercase tracking-widest">{label}</div>
                <Info size={10} className="text-slate-600 flex-shrink-0" />
              </div>
            </Tooltip>
            <div className="text-xl font-black" style={{ color }}>{fmt(animatedValues[key] ?? 0)}</div>
            <div className="text-[10px] text-slate-600 mt-0.5">{sub}</div>
          </motion.div>
        ))}
      </div>

      {/* ── Two-column: gauge + bar chart ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Gauge */}
        <motion.div
          data-section="value_at_risk_gauge"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15 }}
        >
          <ValueAtRiskGauge total={data.action_count} immediate={data.immediate_count} />
        </motion.div>

        {/* Savings bar chart */}
        <motion.div
          data-section="category_breakdown"
          className="glass-card p-6 lg:col-span-2"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Tooltip content={KPI_TIPS.bar_chart} placement="bottom">
            <div className="flex items-center gap-1.5 mb-4 cursor-help">
              <div className="text-xs text-slate-500 uppercase tracking-widest">Savings Realized — by Action Type</div>
              <Info size={10} className="text-slate-600 flex-shrink-0" />
            </div>
          </Tooltip>
          {barData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={barData} barCategoryGap="30%" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                <XAxis
                  dataKey="name"
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => fmt(v)}
                  width={56}
                />
                <RechartsTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {barData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} fillOpacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-slate-500 text-sm">
              No financial data yet — trigger a refresh to generate actions.
            </div>
          )}
        </motion.div>
      </div>

      {/* ── Accepted actions stat ── */}
      {data.accepted_count > 0 && (
        <motion.div
          data-section="accepted_actions"
          className="glass-card p-4 flex items-center gap-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <CheckCircle2 size={18} className="text-emerald-400 flex-shrink-0" />
          <Tooltip content={KPI_TIPS.accepted} placement="top">
            <div className="flex items-center gap-1.5 cursor-help">
              <span className="text-emerald-400 font-bold">{data.accepted_count}</span>
              <span className="text-slate-400 text-sm">
                actions accepted by managers — decisions logged for future model training
              </span>
              <Info size={11} className="text-slate-600 flex-shrink-0" />
            </div>
          </Tooltip>
        </motion.div>
      )}
    </div>
  )
}
